// src/pages/Users.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, Grid, Button, Box, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination, Tooltip,
  IconButton, Select, FormControl, InputLabel, Typography, TextField,
  Checkbox, ListItemText, Chip, Stack, Snackbar, Alert, CircularProgress,
  Divider, Avatar, ToggleButton, ToggleButtonGroup, Menu, Skeleton, TableSortLabel,
} from '@mui/material';
import {
  FilterList, Edit, Add, Search, Download, PeopleAlt, ContentCopy,
  LocationOnOutlined, Security, SupervisorAccount, WorkspacePremium, FilterAlt,
  MoreHoriz,
} from '@mui/icons-material';
import { styled, ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import api from '../api';

/* ----------------------- Theme & styled bits ----------------------- */
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#d32f2f' },
    background: { default: '#F6F8FB', paper: '#ffffff' },
  },
  shape: { borderRadius: 12 },
  typography: {
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiTableRow: { styleOverrides: { root: { '&:hover': { backgroundColor: alpha('#1976d2', 0.06) } } } },
    MuiTableCell: { styleOverrides: { root: { borderBottomColor: alpha('#000', 0.08) } } },
  },
});

const CardShell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  background: `linear-gradient(180deg, ${alpha('#1976d2', 0.06)} 0%, #fff 100%)`,
  boxShadow: '0 8px 22px rgba(0,0,0,0.08)',
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: 14,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  '& .MuiTableCell-head': {
    color: theme.palette.primary.main,
    fontWeight: 700,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
    whiteSpace: 'nowrap',
  },
}));

/* -------------------------- Small helpers -------------------------- */
const roleIcon = (role) => {
  const r = String(role || '').toLowerCase();
  if (r.includes('admin')) return <Security fontSize="inherit" />;
  if (r.includes('manager')) return <SupervisorAccount fontSize="inherit" />;
  if (r.includes('owner')) return <WorkspacePremium fontSize="inherit" />;
  return <MoreHoriz fontSize="inherit" />;
};
const roleColor = (role) => {
  const r = String(role || '').toLowerCase();
  if (r.includes('admin')) return 'error';
  if (r.includes('manager')) return 'warning';
  if (r.includes('lead')) return 'info';
  if (r.includes('owner')) return 'success';
  return 'default';
};
// name -> color for avatar
const stringToColor = (str = 'U') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  let color = '#';
  for (let i = 0; i < 3; i += 1) color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2);
  return color;
};

const LocationChips = ({ value }) => {
  const list = Array.isArray(value)
    ? value
    : String(value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  if (!list.length) return <Typography component="span">—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {list.map((loc) => (
        <Chip
          key={loc}
          size="small"
          variant="outlined"
          icon={<LocationOnOutlined sx={{ fontSize: 16 }} />}
          label={loc}
        />
      ))}
    </Stack>
  );
};

const Highlight = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const q = query.trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
  const parts = String(text ?? '').split(re);
  return (
    <>
      {parts.map((part, i) =>
        re.test(part) ? (
          <Box
            key={i}
            component="mark"
            sx={{
              px: 0.3,
              borderRadius: 0.5,
              backgroundColor: alpha('#1976d2', 0.25),
            }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

/* ----------------------------- Main page ---------------------------- */
const Users = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState({ company: '', location: [], lawarea: '' });
  const [companies, setCompanies] = useState([]);
  const [lawAreas, setLawAreas] = useState([]);
  const [locations, setLocations] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [quickRole, setQuickRole] = useState('All'); // quick filter for role
  const [density, setDensity] = useState('comfortable'); // table density

  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', msg: '' });

  const [exportAnchor, setExportAnchor] = useState(null);

  // sorting
  const [orderBy, setOrderBy] = useState('firstName');
  const [order, setOrder] = useState('asc');

  const navigate = useNavigate();
  const searchRef = useRef(null);

  // focus search with "/" key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // bootstrap
  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoadingInit(true);
        const [{ data: companyData }, lawRes] = await Promise.all([
          api.get('detail/userinfo?view=meta'),
          api.get('tracker/law-area'),
        ]);

        const companyNames = Array.isArray(companyData?.companyDetails?.company_name)
          ? companyData.companyDetails.company_name
          : companyData?.companyDetails?.company_name
            ? [companyData.companyDetails.company_name]
            : [];
        setCompanies(companyNames);

        const rawLoc =
          companyData?.companyDetails?.locations ??
          companyData?.companyDetails?.location ??
          [];
        const asArray = Array.isArray(rawLoc) ? rawLoc : Object.values(rawLoc || {});
        const flat = [...new Set(asArray.flat().filter(Boolean))].sort((a, b) =>
          a.localeCompare(b),
        );
        setLocations(flat);

        const lawAreaArray = Array.isArray(lawRes?.data?.lawareas)
          ? lawRes.data.lawareas.map((la) => la.law_area)
          : [];
        setLawAreas(lawAreaArray);
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, severity: 'error', msg: 'Failed to load initial data.' });
      } finally {
        setLoadingInit(false);
      }
    };
    bootstrap();
  }, []);

  /* ---------------------------- Handlers ---------------------------- */
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    if (name === 'location') {
      setFilters((p) => ({ ...p, location: value.includes('All') ? ['All'] : (Array.isArray(value) ? value : value.split(',')) }));
    } else {
      setFilters((p) => ({ ...p, [name]: value }));
    }
  };

  const handleFilterSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const response = await api.post('detail/Users', {
        company: filters.company,
        location: filters.location,
        lawarea: filters.lawarea,
      });
      setFilteredData(response.data || []);
      setPage(0);
      setSnackbar({ open: true, severity: 'success', msg: 'Filters applied.' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, severity: 'error', msg: 'Could not fetch filtered users.' });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({ company: '', location: [], lawarea: '' });
    setSearchQuery('');
    setQuickRole('All');
    setFilteredData([]);
    setPage(0);
  };

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const onCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setSnackbar({ open: true, severity: 'success', msg: 'Email copied' });
    } catch {
      setSnackbar({ open: true, severity: 'error', msg: 'Copy failed' });
    }
  };

  /* ------------------------- Derived datasets ------------------------ */
  const withNameFields = useMemo(
    () =>
      (filteredData || []).map((u) => {
        const [firstName, ...rest] = String(u?.name ?? '').trim().split(' ');
        return { ...u, firstName, lastName: rest.join(' ') };
      }),
    [filteredData],
  );

  const searched = useMemo(() => {
    if (!debouncedQuery.trim()) return withNameFields;
    const q = debouncedQuery.toLowerCase();
    return withNameFields.filter((u) => {
      const name = `${u.firstName} ${u.lastName}`.toLowerCase();
      const email = String(u?.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [withNameFields, debouncedQuery]);

  const normalizeRole = (r) =>
    String(r || '')
      .toLowerCase()
      .replace(/\s+/g, '');

  const roleFiltered = useMemo(() => {
    if (quickRole === 'All') return searched;
    return searched.filter(
      (u) => normalizeRole(u?.role) === normalizeRole(quickRole),
    );
  }, [searched, quickRole]);

  // sorting
  const comparator = (a, b) => {
    const val = (key, obj) => {
      if (key === 'location') {
        const v = obj.location;
        return Array.isArray(v) ? v.join(',') : (v || '');
      }
      return (obj[key] ?? '').toString();
    };
    const lhs = val(orderBy, a).toLowerCase();
    const rhs = val(orderBy, b).toLowerCase();
    if (lhs < rhs) return order === 'asc' ? -1 : 1;
    if (lhs > rhs) return order === 'asc' ? 1 : -1;
    return 0;
  };

  const sorted = useMemo(() => [...roleFiltered].sort(comparator), [roleFiltered, order, orderBy]);

  const pagedRows = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /* ------------------------------ Export ----------------------------- */
  const exportCSV = (rows) => {
    const headers = ['First Name', 'Last Name', 'E-mail', 'Location', 'Role'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const toLoc = (val) => (Array.isArray(val) ? val.join(', ') : String(val ?? ''));
    const body = rows.map((r) =>
      [r.firstName, r.lastName, r?.email ?? '', toLoc(r?.location), r?.role ?? '']
        .map(esc)
        .join(','),
    );
    const csv = [headers.map(esc).join(','), ...body].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const openExport = (e) => setExportAnchor(e.currentTarget);
  const closeExport = () => setExportAnchor(null);
  const doExportAll = () => { exportCSV(sorted); closeExport(); };
  const doExportPage = () => { exportCSV(pagedRows); closeExport(); };
  const doPrint = () => { closeExport(); window.print(); };

  const tablePadding = density === 'compact' ? 'checkbox' : 'normal'; // 'checkbox' is the tightest

  /* ------------------------------- UI -------------------------------- */
  const showingCount = sorted.length;
  const appliedFilters = !!filters.company || !!filters.lawarea || (filters.location || []).length > 0;

  return (
    <Layout title="Users">
      <ThemeProvider theme={theme}>
        <Box sx={{ background: '#F3F6FB', minHeight: '100%', py: 2 }}>
          <StyledContainer>
            {/* Header */}
            <CardShell sx={{ mb: 2.5, p: 2 }}>
              <Box
                sx={{
                  display: 'flex', alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between', gap: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: '12px', display: 'grid', placeItems: 'center',
                      color: 'common.white', background: 'linear-gradient(135deg,#1976d2 0%,#42a5f5 100%)',
                    }}
                  >
                    <PeopleAlt />
                  </Box>
                  <Box>
                    <Typography variant="h5">Manage Users</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Search, filter, export, and edit user details
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={density}
                    onChange={(_e, v) => v && setDensity(v)}
                    color="primary"
                  >
                    <ToggleButton value="comfortable">Comfort</ToggleButton>
                    <ToggleButton value="compact">Compact</ToggleButton>
                  </ToggleButtonGroup>

                  <Tooltip title="Export">
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={openExport}
                        startIcon={<Download />}
                        disabled={loadingInit || loadingSubmit}
                      >
                        Export
                      </Button>
                    </span>
                  </Tooltip>
                  <Menu open={Boolean(exportAnchor)} anchorEl={exportAnchor} onClose={closeExport}>
                    <MenuItem onClick={doExportAll}>Current results (CSV)</MenuItem>
                    <MenuItem onClick={doExportPage}>Current page (CSV)</MenuItem>
                    <MenuItem onClick={doPrint}>Print</MenuItem>
                  </Menu>

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/add_user')}
                  >
                    New User
                  </Button>
                </Stack>
              </Box>
            </CardShell>

            {/* Filters */}
            <CardShell sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FilterAlt color="primary" />
                  <Typography variant="h6">Filters</Typography>
                </Stack>
                <Tooltip title="Filter List">
                  <IconButton color="primary">
                    <FilterList />
                  </IconButton>
                </Tooltip>
              </Box>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Company</InputLabel>
                    <Select value={filters.company} onChange={handleFilterChange} name="company" label="Company">
                      {companies.map((c, i) => (
                        <MenuItem key={i} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      multiple
                      value={Array.isArray(filters.location) ? filters.location : []}
                      onChange={handleFilterChange}
                      name="location"
                      label="Location"
                      renderValue={(selected) => selected.includes('All') ? 'All' : selected.join(', ')}
                    >
                      <MenuItem value="All">
                        <Checkbox checked={filters.location.includes('All')} />
                        <ListItemText primary="All" />
                      </MenuItem>
                      {locations.map((city) => (
                        <MenuItem key={city} value={city}>
                          <Checkbox checked={filters.location.includes(city)} />
                          <ListItemText primary={city} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Law Area</InputLabel>
                    <Select value={filters.lawarea} onChange={handleFilterChange} name="lawarea" label="Law Area">
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="All"><em>All</em></MenuItem>
                      {lawAreas.map((la, i) => <MenuItem key={i} value={la}>{la}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Apply / Clear (spinner overlay to keep width) */}
                <Grid item xs={12} md={2}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleFilterSubmit}
                      disabled={loadingInit || loadingSubmit}
                      fullWidth
                      startIcon={!loadingSubmit ? <Search /> : null}
                      sx={{ minWidth: 120, position: 'relative' }}
                    >
                      <Box component="span" sx={{ visibility: loadingSubmit ? 'hidden' : 'visible' }}>
                        Apply
                      </Box>
                      {loadingSubmit && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }}
                        />
                      )}
                    </Button>

                    <Button variant="outlined" color="secondary" onClick={handleClearFilters} fullWidth>
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  inputRef={searchRef}
                  variant="outlined"
                  label="Search by name or email  (press / )"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ endAdornment: <Search color="primary" /> }}
                />
              </Grid>

              {/* Quick role filters */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  {appliedFilters
                    ? `Showing ${showingCount} result${showingCount !== 1 ? 's' : ''}`
                    : 'No filters applied'}
                </Typography>
                {[
                  { value: 'All', label: 'All roles' },
                  { value: 'owner', label: 'owner' },
                  { value: 'manager', label: 'manager' },
                  { value: 'seniormanager', label: 'Senior Manager' },
                  { value: 'admin', label: 'admin' },
                ].map(({ value, label }) => (
                  <Chip
                    key={value}
                    size="small"
                    color={value === quickRole ? 'primary' : 'default'}
                    variant={value === quickRole ? 'filled' : 'outlined'}
                    onClick={() => setQuickRole(value)}
                    icon={value !== 'All' ? roleIcon(value) : undefined}
                    label={label}
                  />
                ))}

                {filters.company && <Chip size="small" label={filters.company} color="primary" variant="soft" />}
                {filters.lawarea && <Chip size="small" label={filters.lawarea} color="info" variant="soft" />}
                {filters.location?.includes('All') ? (
                  <Chip size="small" label="All locations" color="secondary" variant="soft" />
                ) : (
                  (filters.location || []).slice(0, 3).map((l) => (
                    <Chip key={l} size="small" label={l} color="secondary" variant="soft" />
                  ))
                )}
                {filters.location && filters.location.length > 3 && (
                  <Chip size="small" label={`+${filters.location.length - 3}`} variant="outlined" />
                )}
              </Stack>
            </CardShell>

            {/* Table */}
            <TableContainer component={Paper} sx={{ mt: 2, boxShadow: '0 10px 28px rgba(0,0,0,0.07)' }}>
              <Table size={density === 'compact' ? 'small' : 'medium'} padding={tablePadding}>
                <StyledTableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'firstName' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'firstName'}
                        direction={orderBy === 'firstName' ? order : 'asc'}
                        onClick={() => handleRequestSort('firstName')}
                      >
                        First Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'lastName' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'lastName'}
                        direction={orderBy === 'lastName' ? order : 'asc'}
                        onClick={() => handleRequestSort('lastName')}
                      >
                        Last Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'email' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                      >
                        E-mail
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'location' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'location'}
                        direction={orderBy === 'location' ? order : 'asc'}
                        onClick={() => handleRequestSort('location')}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'role' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'role'}
                        direction={orderBy === 'role' ? order : 'asc'}
                        onClick={() => handleRequestSort('role')}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Edit</TableCell>
                  </TableRow>
                </StyledTableHead>

                <TableBody>
                  {/* Loading skeletons */}
                  {loadingInit &&
                    Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        <TableCell><Skeleton variant="text" width={120} /></TableCell>
                        <TableCell><Skeleton variant="text" width={140} /></TableCell>
                        <TableCell><Skeleton variant="text" width={220} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={180} height={28} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={90} height={28} /></TableCell>
                        <TableCell align="center"><Skeleton variant="circular" width={28} height={28} /></TableCell>
                      </TableRow>
                    ))}

                  {!loadingInit && pagedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        {/* tiny empty-state illustration */}
                        <Box sx={{ mb: 1 }}>
                          <svg width="96" height="60" viewBox="0 0 96 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="8" y="14" width="80" height="36" rx="6" fill="#E3F2FD" />
                            <rect x="16" y="22" width="44" height="6" rx="3" fill="#BBDEFB" />
                            <rect x="16" y="32" width="28" height="6" rx="3" fill="#BBDEFB" />
                            <circle cx="74" cy="30" r="10" fill="#90CAF9" />
                          </svg>
                        </Box>
                        <Typography variant="h6" gutterBottom>No users found</Typography>
                        <Typography color="text.secondary">Try adjusting filters or your search.</Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loadingInit &&
                    pagedRows.map((row) => {
                      const { firstName, lastName } = row;
                      const fullName = `${firstName} ${lastName}`.trim();
                      return (
                        <TableRow
                          key={row?._id || row?.email}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:nth-of-type(odd)': { backgroundColor: alpha('#1976d2', 0.025) },
                          }}
                          onDoubleClick={() => navigate(`/edit-user/${row?._id}`)}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                              <Avatar
                                alt={fullName || 'User'}
                                sx={{ width: 28, height: 28, bgcolor: stringToColor(fullName || 'U'), fontSize: 12 }}
                              >
                                {(firstName?.[0] || 'U').toUpperCase()}
                              </Avatar>
                              <Typography variant="body2">
                                <Highlight text={firstName} query={debouncedQuery} />
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              <Highlight text={lastName} query={debouncedQuery} />
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2">
                                <Highlight text={row?.email || '—'} query={debouncedQuery} />
                              </Typography>
                              {row?.email && (
                                <Tooltip title="Copy email">
                                  <IconButton size="small" onClick={() => onCopy(row.email)}>
                                    <ContentCopy fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <LocationChips value={row?.location} />
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={row?.role || '—'}
                              color={roleColor(row?.role)}
                              icon={roleIcon(row?.role)}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/edit-user/${row?._id}`);
                                }}
                                size="small"
                                color="primary"
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={sorted.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              />
            </TableContainer>
          </StyledContainer>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.msg}
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default Users;
